import { useEffect, useState } from "react";
import axios from "axios";
import { MapPin } from "lucide-react";
import BeforeAfterSlider from "./BeforeAfterSlider";
import { useT } from "../lib/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const REALISATION_CATEGORIES = [
  { key: "jardins", label: "Jardins", en: "Gardens" },
  { key: "frelons", label: "Frelons", en: "Hornets" },
  { key: "nuisibles", label: "Autres nuisibles", en: "Other pests" },
];

const FILTERS = [{ key: "all", label: "Tout voir", en: "View all" }, ...REALISATION_CATEGORIES];

const TXT = {
  fr: { eyebrow: "Avant / après", t1: "Nos", t2: "réalisations", intro: "Faites glisser le curseur pour comparer l'avant et l'après de nos chantiers en Creuse.", empty: "Aucune réalisation dans cette catégorie pour le moment." },
  en: { eyebrow: "Before / after", t1: "Our", t2: "projects", intro: "Drag the slider to compare before and after on our projects in Creuse.", empty: "No projects in this category yet." },
};

export default function Realisations() {
  const { lang } = useT();
  const en = lang === "en";
  const tx = TXT[en ? "en" : "fr"];
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    axios.get(`${API}/realisations`).then((r) => setItems(r.data)).catch(() => {});
  }, []);

  if (items.length === 0) return null;
  const shown = filter === "all" ? items : items.filter((i) => i.category === filter);
  const labelOf = (k) => {
    const c = REALISATION_CATEGORIES.find((x) => x.key === k);
    return c && (en ? c.en : c.label);
  };

  return (
    <section id="realisations" data-testid="realisations-section" className="py-24 md:py-32 bg-[#FDFDFC]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
          <div className="max-w-2xl">
            <div className="text-sm tracking-[0.25em] uppercase text-[#1B5E20] font-medium mb-4">{tx.eyebrow}</div>
            <h2 className="font-['Outfit'] text-4xl sm:text-5xl font-light tracking-tight text-[#1A2E1C] leading-tight">
              {tx.t1} <span className="text-[#1B5E20] font-medium">{tx.t2}</span>
            </h2>
            <p className="mt-4 text-[#5C705E]">{tx.intro}</p>
          </div>
          <div className="flex flex-wrap gap-2" role="tablist">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                role="tab"
                aria-selected={filter === f.key}
                onClick={() => setFilter(f.key)}
                data-testid={`realisations-filter-${f.key}`}
                className={`px-5 py-2.5 rounded-full text-sm font-medium border transition-colors ${
                  filter === f.key
                    ? "bg-[#1B5E20] text-white border-[#1B5E20]"
                    : "bg-white text-[#1A2E1C] border-[#E5EBE5] hover:border-[#1B5E20]"
                }`}
              >
                {en ? f.en : f.label}
              </button>
            ))}
          </div>
        </div>

        {shown.length === 0 ? (
          <p data-testid="realisations-empty" className="text-center text-[#5C705E] py-12">
            {tx.empty}
          </p>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {shown.map((item) => (
              <article key={item.id} data-testid={`realisation-${item.id}`} className="bg-white rounded-3xl border border-[#E5EBE5] overflow-hidden hover:shadow-xl transition-shadow">
                <BeforeAfterSlider item={item} en={en} />
                <div className="p-5">
                  <div className="text-xs uppercase tracking-wider text-[#1B5E20] font-semibold mb-1">{labelOf(item.category)}</div>
                  <h3 className="font-['Outfit'] font-semibold text-[#1A2E1C]">{item.title}</h3>
                  {item.location && (
                    <div className="mt-1 text-sm text-[#5C705E] flex items-center gap-1">
                      <MapPin className="w-3.5 h-3.5" /> {item.location}
                    </div>
                  )}
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
